import dayjs from "dayjs";
import { UCUM_STEPS } from "@/services/fhir/observation/wearableObservationBuilder";

/** Duração e período do sono (LOINC 93832-4). */
export function getSleepTotalDisplay(resource: any) {
  if (!resource) {
    return {
      durationText: "-",
      periodText: "",
      noteText: "",
      start: undefined as string | undefined,
      end: undefined as string | undefined,
    };
  }

  const value = resource?.valueQuantity?.value;
  const unit = resource?.valueQuantity?.unit || resource?.valueQuantity?.code;

  let durationText = "-";

  if (value !== undefined && (unit === "min" || unit === "s")) {
    if (unit === "min") {
      const totalMinutes = Math.round(value as number);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      durationText = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
    } else if (unit === "s") {
      const totalSeconds = Math.round(value as number);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      durationText = minutes > 0 ? `${minutes}min ${seconds}s` : `${seconds}s`;
    }
  }

  const start = resource?.effectivePeriod?.start
    ? dayjs(resource.effectivePeriod.start).format("HH:mm")
    : undefined;
  const end = resource?.effectivePeriod?.end
    ? dayjs(resource.effectivePeriod.end).format("HH:mm")
    : undefined;

  const periodText =
    start && end ? `${start} - ${end}` : start ? `A partir de ${start}` : "";

  const noteText =
    resource?.note && resource.note.length > 0 && resource.note[0]?.text
      ? resource.note[0].text
      : "";

  return {
    durationText,
    periodText,
    noteText,
    start,
    end,
  };
}

/** Temperatura da pele no sono (81264-4) e componentes. */
export function getSleepSkinTempDisplay(resource: any) {
  if (!resource) {
    return {
      minText: "-",
      avgText: "-",
      maxText: "-",
      minTime: "",
      maxTime: "",
      avgTime: "",
      avgValue: null,
      minValue: null,
      maxValue: null,
      unit: "°C",
    };
  }

  const mainValue = resource?.valueQuantity?.value;
  const mainUnit = resource?.valueQuantity?.unit || resource?.valueQuantity?.code;

  const components = Array.isArray(resource?.component) ? resource.component : [];

  const componentHasCode = (c: any, code: string) =>
    Array.isArray(c?.code?.coding) &&
    c.code.coding.some((coding: any) => String(coding?.code) === code);

  const minComponent = components.find(
    (c: any) =>
      componentHasCode(c, "8320-4") ||
      c?.code?.text?.toLowerCase().includes("mínima")
  );

  const maxComponent = components.find(
    (c: any) =>
      componentHasCode(c, "8315-4") ||
      c?.code?.text?.toLowerCase().includes("máxima")
  );

  const minTimeComponent = components.find(
    (c: any) =>
      componentHasCode(c, "sleep-skin-temp-min-time") ||
      c?.code?.text?.toLowerCase().includes("horário da temperatura mínima da pele")
  );

  const maxTimeComponent = components.find(
    (c: any) =>
      componentHasCode(c, "sleep-skin-temp-max-time") ||
      c?.code?.text?.toLowerCase().includes("horário da temperatura máxima da pele")
  );

  // Horário da média, se o app enviar (opcional)
  const avgTimeComponent = components.find(
    (c: any) =>
      componentHasCode(c, "sleep-skin-temp-avg-time") ||
      c?.code?.text?.toLowerCase().includes("horário da temperatura média")
  );

  const formatTemp = (value: number | undefined, unit: string | undefined) => {
    if (value === undefined) return "-";
    if (!unit) return `${value.toFixed(1)}`;
    return `${value.toFixed(1)} ${unit}`;
  };

  const formatTimeFromComponent = (component: any) => {
    if (!component) return "";

    const rawValue: string | undefined =
      component.valueDateTime ?? component.valueTime ?? component.valueString;

    if (!rawValue || typeof rawValue !== "string") return "";

    const str = String(rawValue).trim();

    const parsed = dayjs(str);
    if (parsed.isValid()) {
      return parsed.format("HH:mm");
    }

    const match = str.match(/T(\d{1,2}):(\d{2})/);
    if (match) {
      const h = match[1].padStart(2, "0");
      const m = match[2];
      return `${h}:${m}`;
    }

    return "";
  };

  const minValue = minComponent?.valueQuantity?.value as number | undefined;
  const minUnit =
    minComponent?.valueQuantity?.unit || minComponent?.valueQuantity?.code;

  const maxValue = maxComponent?.valueQuantity?.value as number | undefined;
  const maxUnit =
    maxComponent?.valueQuantity?.unit || maxComponent?.valueQuantity?.code;

  const minTime = formatTimeFromComponent(minTimeComponent);
  const maxTime = formatTimeFromComponent(maxTimeComponent);
  const avgTime = formatTimeFromComponent(avgTimeComponent);

  const minText = minValue !== undefined ? formatTemp(minValue, minUnit) : "-";
  const avgText =
    mainValue !== undefined ? formatTemp(mainValue, mainUnit) : "-";
  const maxText = maxValue !== undefined ? formatTemp(maxValue, maxUnit) : "-";

  return {
    minText,
    avgText,
    maxText,
    minTime,
    maxTime,
    avgTime,
    // Números brutos para os gráficos de período (média / mín / máx)
    avgValue:
      mainValue !== undefined && Number.isFinite(Number(mainValue))
        ? Number(mainValue)
        : null,
    minValue:
      minValue !== undefined && Number.isFinite(Number(minValue))
        ? Number(minValue)
        : null,
    maxValue:
      maxValue !== undefined && Number.isFinite(Number(maxValue))
        ? Number(maxValue)
        : null,
    unit: mainUnit || minUnit || maxUnit || "°C",
  };
}

/**
 * Formata duração preservando segundos quando existirem.
 * - unit "s": sempre pode mostrar s (e h/min se couber)
 * - unit "min": se o valor for fracionário (ex.: 5.5), converte e mostra os segundos;
 *   se for inteiro, mantém só h/min (não inventa 0s)
 */
function formatDurationWithSeconds(value: number, unit: "min" | "s"): string {
  const totalSeconds =
    unit === "s" ? Math.round(value) : Math.round(value * 60);

  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "-";

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || (hours > 0 && seconds > 0)) parts.push(`${minutes}min`);
  // Só inclui segundos quando houver resto (ou valor só em segundos)
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(" ");
}

/**
 * Formata valor de exibição de uma Observation FHIR (wearable / mesmo conjunto do diálogo de dispositivos).
 */
export function formatWearableObservationValue(resource: any): string {
  if (!resource) return "-";

  if (resource?.code?.coding?.[0]?.code === "93832-4") {
    // Nota e despertar têm seções próprias — não misturar no valor da duração
    const { durationText, periodText, start, end } =
      getSleepTotalDisplay(resource);

    const pieces = [durationText];
    if (start && end) {
      pieces.push(`Início ${start} · Fim ${end}`);
    } else if (periodText) {
      pieces.push(`(${periodText})`);
    }

    return pieces.join(" | ");
  }

  if (resource?.code?.coding?.[0]?.code === "81264-4") {
    const { minText, avgText, maxText, minTime, maxTime, avgTime } =
      getSleepSkinTempDisplay(resource);

    const minPart = minTime ? `Mín ${minText} (${minTime})` : `Mín ${minText}`;
    const avgPart = avgTime ? `Méd ${avgText} (${avgTime})` : `Méd ${avgText}`;
    const maxPart = maxTime ? `Máx ${maxText} (${maxTime})` : `Máx ${maxText}`;

    return `${avgPart} | ${minPart} | ${maxPart}`;
  }

  if (resource?.code?.coding?.[0]?.code === "85354-9") {
    const sistolica =
      resource?.component?.[0]?.valueQuantity?.value ?? undefined;
    const diastolica =
      resource?.component?.[1]?.valueQuantity?.value ?? undefined;

    if (sistolica !== undefined && diastolica !== undefined) {
      return `${sistolica}/${diastolica} mmHg`;
    }
  }

  if (resource?.valueCodeableConcept) {
    return (
      resource.valueCodeableConcept?.coding?.[0]?.display ||
      resource.valueCodeableConcept?.text ||
      "-"
    );
  }

  if (resource?.valueQuantity) {
    const value = resource.valueQuantity.value;
    const unit = resource.valueQuantity.unit;
    const code = resource.valueQuantity.code;

    if (
      unit === "count" ||
      code === "1" ||
      unit === UCUM_STEPS ||
      code === UCUM_STEPS
    ) {
      if (value !== undefined) {
        return `${value}`;
      }
    }

    if (code === "kg" && typeof value === "number") {
      return `${value.toFixed(1)} kg`;
    }

    if ((code === "kg/m2" || unit === "kg/m2") && typeof value === "number") {
      return `${value.toFixed(1)} kg/m²`;
    }

    if (
      (code === "kcal/d" || unit === "kcal/d") &&
      typeof value === "number"
    ) {
      return `${Math.round(value)} kcal/dia`;
    }

    if ((code === "km/h" || unit === "km/h") && typeof value === "number") {
      return `${value.toFixed(1)} km/h`;
    }

    if (
      (code === "mL/min/kg" || unit === "mL/min/kg") &&
      typeof value === "number"
    ) {
      return `${value.toFixed(1)} mL/min/kg`;
    }

    if ((unit === "%" || code === "%") && typeof value === "number") {
      return `${value.toFixed(1)} %`;
    }

    if (
      (unit === "beats/min" || code === "/min") &&
      typeof value === "number"
    ) {
      return `${value} bpm`;
    }

    // Glicose (LOINC 2339-0): mg/dL
    if (
      (unit === "mg/dL" ||
        unit === "mg/dl" ||
        code === "mg/dL" ||
        code === "mg/dl") &&
      typeof value === "number"
    ) {
      return `${Math.round(value)} mg/dL`;
    }

    if (
      unit === "min" ||
      unit === "s" ||
      code === "min" ||
      code === "s"
    ) {
      const durationUnit = (unit || code) as "min" | "s";
      if (value === undefined || (durationUnit !== "min" && durationUnit !== "s")) {
        return "-";
      }
      return formatDurationWithSeconds(value as number, durationUnit);
    }

    if (value !== undefined && unit) return `${value} ${unit}`;
    if (value !== undefined) return `${value}`;
    if (unit) return unit;
  }

  if (resource?.valueString) {
    return resource.valueString;
  }

  if (resource?.valueInteger !== undefined) {
    return resource.valueInteger.toString();
  }

  return "-";
}

/** Seções do diálogo de dispositivos (FluxHealth / wearable). */
export type WearableObservationSectionId =
  | "exercise"
  | "bodyComposition"
  | "screenTime"
  | "general"
  | "sleep";

/** LOINCs enviados pela composição corporal do FluxHealth. */
export const BODY_COMPOSITION_LOINC_CODES = new Set([
  "29463-7", // peso
  "8302-2", // altura
  "39156-5", // IMC
  "41982-0", // % gordura
  "73708-0", // massa de gordura
  "73965-6", // % músculo esquelético
  "73964-9", // massa muscular esquelética
  "8342-8", // massa livre de gordura
  "101683-1", // água corporal total
  "41979-6", // taxa metabólica basal
]);

/** Código raiz da Observation de tempo de tela (CodeSystem digital-wellbeing). */
export const SCREEN_TIME_CODE = "screen-time";

/** Código raiz da Observation de sessão de exercício (CodeSystem exercise-wearable). */
export const EXERCISE_SESSION_CODE = "exercise-session";

/** Extrai o nome do dispositivo de Observation.method (uma vez por seção). */
export function getObservationDeviceName(resource: any): string | undefined {
  const fromText =
    typeof resource?.method?.text === "string"
      ? resource.method.text.trim()
      : "";
  if (fromText) return fromText;

  const coding = Array.isArray(resource?.method?.coding)
    ? resource.method.coding
    : [];
  const fromCoding = coding.find(
    (c: any) => typeof c?.display === "string" && c.display.trim()
  );
  return fromCoding?.display?.trim() || undefined;
}

/** Código principal da Observation (primeiro coding com code). */
export function getObservationPrimaryCode(resource: any): string {
  const coding = Array.isArray(resource?.code?.coding)
    ? resource.code.coding
    : [];
  const found = coding.find((c: any) => c?.code);
  return found?.code ? String(found.code) : "";
}

/**
 * Data civil YYYY-MM-DD pelo prefixo ISO (evita shift UTC→BRT no dia).
 */
export function toWearableDayKey(raw: unknown): string | null {
  if (raw == null || raw === "") return null;
  const str = String(raw).trim();
  const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = dayjs(str);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

/** LOINCs FluxHealth de sono / SpO2 / temperatura cutânea. */
export const SLEEP_FAMILY_LOINC_CODES = new Set([
  "93832-4", // Sono total
  "93828-2", // Acordado
  "93829-0", // REM
  "93830-8", // Leve
  "93831-6", // Profundo
  "96634-8", // SpO2 < 90%
  "59408-5", // SpO2
  "81264-4", // Temperatura
]);

/**
 * Dia canônico da Observation (detalhe do dia + gráficos).
 * Sono: início da sessão (noite em que dormiu) — alinhado ao sleepWakeDay.
 * Tela: fim do período de uso.
 * Demais: effectiveDateTime → period → issued.
 *
 * Usado para NÃO misturar sono da noite anterior no chip de envio
 * de tempo de tela do dia seguinte (lookback era só da busca).
 */
export function getWearableObservationDayKey(resource: any): string | null {
  const code = getObservationPrimaryCode(resource);

  if (SLEEP_FAMILY_LOINC_CODES.has(code)) {
    return (
      toWearableDayKey(resource?.effectivePeriod?.start) ??
      toWearableDayKey(resource?.effectivePeriod?.end) ??
      toWearableDayKey(resource?.effectiveDateTime) ??
      toWearableDayKey(resource?.issued)
    );
  }

  if (code === SCREEN_TIME_CODE) {
    return (
      toWearableDayKey(resource?.effectivePeriod?.end) ??
      toWearableDayKey(resource?.effectiveDateTime) ??
      toWearableDayKey(resource?.effectivePeriod?.start) ??
      toWearableDayKey(resource?.issued)
    );
  }

  return (
    toWearableDayKey(resource?.effectiveDateTime) ??
    toWearableDayKey(resource?.effectivePeriod?.end) ??
    toWearableDayKey(resource?.effectivePeriod?.start) ??
    toWearableDayKey(resource?.issued)
  );
}

/** Classifica a Observation na seção correta do diálogo. */
export function getWearableObservationSectionId(
  resource: any
): WearableObservationSectionId {
  const code = getObservationPrimaryCode(resource);
  const text = (
    resource?.code?.text ||
    resource?.code?.coding?.[0]?.display ||
    ""
  ).toLowerCase();

  if (code === SCREEN_TIME_CODE || text.includes("tempo de tela")) {
    return "screenTime";
  }
  if (
    code === EXERCISE_SESSION_CODE ||
    text.includes("sessão de exercício") ||
    text.includes("sessao de exercicio")
  ) {
    return "exercise";
  }
  if (BODY_COMPOSITION_LOINC_CODES.has(code)) {
    return "bodyComposition";
  }
  // Por LOINC (texto EN do MHD não começa com "sono"/"spo2")
  if (
    SLEEP_FAMILY_LOINC_CODES.has(code) ||
    text.startsWith("sono") ||
    text.startsWith("spo2")
  ) {
    return "sleep";
  }
  return "general";
}

/** Rótulo amigável do card (ajustes pontuais de texto). */
export function getWearableObservationLabel(resource: any): string {
  let label =
    resource?.code?.text ||
    resource?.code?.coding?.[0]?.display ||
    "Observação";

  if (label === "SpO2 Tempo abaixo de 90%") label = "SpO2 abaixo de 90%";
  if (label === "Calorias (Consumidas)") label = "Calorias";
  if (label === "Peso corporal") label = "Peso";
  // LOINC 2339-0 — rótulo único em todas as abas / períodos
  if (
    getObservationPrimaryCode(resource) === "2339-0" ||
    /^glicose/i.test(label)
  ) {
    label = "Glicose no sangue";
  }

  return label;
}

/** Formata o valor de um component FHIR (quantity / codeableConcept). */
export function formatObservationComponentValue(component: any): string {
  if (!component) return "-";

  if (component.valueCodeableConcept) {
    return (
      component.valueCodeableConcept?.text ||
      component.valueCodeableConcept?.coding?.[0]?.display ||
      "-"
    );
  }

  // Reaproveita a formatação de valueQuantity do formatter principal
  return formatWearableObservationValue({
    valueQuantity: component.valueQuantity,
    valueString: component.valueString,
    valueInteger: component.valueInteger,
  });
}

type SpO2Role = "min" | "avg" | "unknown";

export interface SpO2Candidate {
  value: number;
  formatted: string;
  role: SpO2Role;
  unit?: string;
  code?: string;
}

function spo2FieldLabel(field: any): string {
  return String(
    field?.code?.text ||
      field?.code?.coding?.[0]?.display ||
      field?.code?.coding?.[0]?.code ||
      ""
  ).toLowerCase();
}

function spo2RoleFromLabel(label: string): SpO2Role {
  if (
    label.includes("mínima") ||
    label.includes("minima") ||
    label.includes("minimum") ||
    label.includes("lowest")
  ) {
    return "min";
  }
  if (
    label.includes("média") ||
    label.includes("media") ||
    label.includes("average") ||
    label.includes("mean")
  ) {
    return "avg";
  }
  return "unknown";
}

/**
 * Lista bruta de valores SpO2 da Observation (components + valueQuantity).
 * Usado na visão do dia e na agregação por dia dos gráficos de período.
 */
export function collectSpO2Candidates(resource: any): SpO2Candidate[] {
  if (!resource) return [];

  const candidates: SpO2Candidate[] = [];
  const seen = new Set<number>();

  const pushQty = (qty: any, role: SpO2Role) => {
    const raw = qty?.value;
    if (typeof raw !== "number" || Number.isNaN(raw)) return;
    if (seen.has(raw)) {
      const existing = candidates.find((c) => c.value === raw);
      if (existing && existing.role === "unknown" && role !== "unknown") {
        existing.role = role;
      }
      return;
    }
    seen.add(raw);
    candidates.push({
      value: raw,
      formatted: formatWearableObservationValue({ valueQuantity: qty }),
      role,
      unit: qty?.unit,
      code: qty?.code,
    });
  };

  const components = Array.isArray(resource?.component)
    ? resource.component
    : [];

  components.forEach((component: any) => {
    if (!component?.valueQuantity) return;
    pushQty(
      component.valueQuantity,
      spo2RoleFromLabel(spo2FieldLabel(component))
    );
  });

  if (resource?.valueQuantity?.value !== undefined) {
    pushQty(resource.valueQuantity, "unknown");
  }

  return candidates;
}

/**
 * A partir dos candidatos (de uma Observation ou de várias do mesmo dia),
 * define mínima e média. Sem rótulos claros: menor = mínima, maior = média.
 */
export function resolveSpO2Parts(candidates: SpO2Candidate[]): {
  min?: SpO2Candidate;
  avg?: SpO2Candidate;
} {
  if (candidates.length === 0) return {};

  const labeledMin = candidates.find((c) => c.role === "min");
  const labeledAvg = candidates.find((c) => c.role === "avg");

  if (labeledMin && labeledAvg && labeledMin.value !== labeledAvg.value) {
    return { min: labeledMin, avg: labeledAvg };
  }

  if (candidates.length >= 2) {
    const sorted = [...candidates].sort((a, b) => a.value - b.value);
    return {
      min: sorted[0],
      avg: sorted[sorted.length - 1],
    };
  }

  return { avg: candidates[0] };
}

/**
 * SpO2 (59408-5): extrai mínima e média de uma Observation.
 * Preferência pelos rótulos; se vierem dois valores sem rótulo claro,
 * o menor é a mínima e o maior a média.
 */
export function getSpO2DisplayParts(resource: any): {
  min?: { value: number; formatted: string };
  avg?: { value: number; formatted: string };
} {
  const { min, avg } = resolveSpO2Parts(collectSpO2Candidates(resource));
  return {
    ...(min ? { min: { value: min.value, formatted: min.formatted } } : {}),
    ...(avg ? { avg: { value: avg.value, formatted: avg.formatted } } : {}),
  };
}

/** Linhas de component para exibir em sessão de exercício / tempo de tela. */
export function getObservationComponentRows(
  resource: any
): { label: string; value: string }[] {
  const components = Array.isArray(resource?.component)
    ? resource.component
    : [];

  return components
    .map((component: any) => {
      const label =
        component?.code?.text ||
        component?.code?.coding?.[0]?.display ||
        "Item";
      return {
        label,
        value: formatObservationComponentValue(component),
      };
    })
    .filter((row: { label: string; value: string }) => row.value !== "-");
}

/** Nome único do dispositivo para o cabeçalho da seção (não repetir nos cards). */
export function getSectionDeviceName(entries: any[]): string | undefined {
  const names = new Set<string>();
  entries.forEach((entry) => {
    const name = getObservationDeviceName(entry?.resource);
    if (name) names.add(name);
  });
  if (names.size === 0) return undefined;
  return Array.from(names).join(" · ");
}
